import React, { useContext } from 'react';
import { Link as GatsbyLink, navigate } from 'gatsby';
import { BASContext } from '../../../../providers/basContext';
import { appendQueryParams } from '../../../../utils/bas/appendQueryParams';
import getSplitUrlExperiment from '../../../../utils/getSplitUrlExperiment';
import getIfSimpleRedirect from '../../../../utils/getIfSimpleRedirect';
import { filterUTMParams } from '../../../../utils/bas/filterUTMparams';
import useHasMounted from '../../../../utils/hooks/useHasMounted';

import { StyledMuiExternalLink, StyledMuiLink } from './LinkStyles';

/**
 * Basic normal case text links without an icon
 * https://mui.com/material-ui/api/link/
 *
 * @param {String} [activeClassName]
 * @param {React.ReactNode} children
 * @param {String} [className]
 * @param {{ eventAction: string, eventCategory: string }} [gtmEvent]
 * @param {{ gtmClassName: string, gtmElementId: string }} [gtmTrackingInfo]
 * @param {Boolean} isInternal
 * @param {Boolean} noFollow
 * @param {String} [rel = noopener noreferrer]
 * @param {String} [target = _blank]
 * @param {'none' | 'hover' | 'always'} [underline = none]
 * @param {String} url
 * @param {String} variant
 * @param {'primary' | 'secondary'} color
 * @param {Number} tabIndex
 *
 * @param {React.ForwardedRef} ref
 *
 * @returns {React.ReactElement}
 */
const Link = React.forwardRef(
  (
    {
      activeClassName,
      children,
      className,
      gtmEvent = {},
      gtmTrackingInfo,
      rel = 'noopener noreferrer',
      target = '_blank',
      underline = 'none',
      url,
      variant,
      color,
      tabIndex = 0,
      ariaLabel,
      noFollow,
      external,
    },
    ref,
  ) => {
    const isWindowAvailable = useHasMounted();
    const { gtmElementId, gtmClassName } = gtmTrackingInfo || {};
    const gatsbyLinkClassNames = `${className} ${gtmClassName}`.replace(/undefined\s?/g, '');
    const ifItsARedirect = !!getIfSimpleRedirect(url) || !!getSplitUrlExperiment(url);

    const isInternal =
      isWindowAvailable && (url.includes(window.location.host) || (/^\/(?!\/)/.test(url) && !external)); // check if link is internal if window is defined

    const buildQueryString = useContext(BASContext);
    const utmParams = filterUTMParams(buildQueryString);

    const linkImpressOrSubdomain = url?.includes('smile2impress.com') || url?.includes('booking.drsmile');

    // get URL with parameters if GATSBY_PERSIST_UTM is true
    const urlWithPersistentParams =
      process.env.GATSBY_PERSIST_UTM === 'true'
        ? appendQueryParams(url, buildQueryString)
        : linkImpressOrSubdomain
        ? appendQueryParams(url, utmParams)
        : url;

    const handleClick = event => {
      event.preventDefault();

      if (ifItsARedirect) {
        window.location.href = urlWithPersistentParams;
        return false;
      }
      if (!isInternal) {
        window.open(urlWithPersistentParams, target);
        return false;
      }

      return navigate(urlWithPersistentParams, { state: gtmEvent });
    };

    return ifItsARedirect || !isInternal ? (
      <StyledMuiExternalLink
        id={gtmElementId}
        ref={ref}
        className={className}
        variant={variant}
        href={linkImpressOrSubdomain ? urlWithPersistentParams : url}
        onClick={process.env.GATSBY_PERSIST_UTM === 'true' && ifItsARedirect ? handleClick : undefined}
        target={ifItsARedirect ? '_self' : target}
        rel={noFollow ? 'nofollow' : rel}
        underline={underline}
        color={color}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
      >
        {children}
      </StyledMuiExternalLink>
    ) : (
      <StyledMuiLink
        ref={ref}
        component={GatsbyLink}
        id={gtmElementId}
        className={className || gtmClassName ? gatsbyLinkClassNames : undefined}
        activeClassName={activeClassName}
        variant={variant}
        to={url}
        onClick={isInternal && process.env.GATSBY_PERSIST_UTM === 'true' ? handleClick : undefined}
        state={gtmEvent}
        underline={underline}
        color={color}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
      >
        {children}
      </StyledMuiLink>
    );
  },
);

export default Link;
